import { FC, useEffect, useState } from 'react';

import { useCollapsed, useSetCollapsed } from '~features/shared/collapse';

// import { LinksView } from '~entities/links';
// import { usePhoto, useSetPhoto } from '~entities/photo';
import { useProfile } from '~entities/kindergarten/profile';
import { SiderUser, roleType, useUser } from '~entities/shared/user';
import { useHasVoucher, useSetHasVoucher } from '~entities/parent/voucher';
import {
  useParentNotificationUnreadCount,
  useRefetchParentNotificationUnreadCount,
} from '~entities/parent/parentNotificationHistory';
import {
  useParentVoucherNotificationsUnreadCount,
  useRefetchParentVoucherNotificationsUnreadCount,
} from '~entities/parent/parentVoucherNotifications';
import { useTranslation } from '~shared/lib/i18n';

import { RoutesUrls } from '~shared/lib/router';

import {
  AttendanceIcon,
  DocIcon,
  DocsIcon,
  EducationIcon,
  EmployeesIcon,
  FeedbackIcon,
  FileIcon,
  FoodIcon,
  GroupIcon,
  HelpIcon,
  KidsIcon,
  OrganizationIcon,
  ParentsMembers,
  ParentsVoucherRoute,
  SN,
  SettingsIcon,
  Sider,
  SiderButton,
  SiderSettingsButton,
  TableIcon,
  UpdateIcon,
  VaucherIcon,
  useWindowInnerWidth,
} from '~shared/ui';
import { HomeIcon, LogoutIcon, MinistriesIcon, NotificationIcon, PersonIcon } from '~shared/ui';
import { useOrgInfo, useSetOrgInfo } from '~entities/kindergarten/organization';

const ApiUrl = import.meta.env.VITE_API_URL;

export interface NavigationProps {
  showLogout?: boolean;
}

export const Navigation: FC<NavigationProps> = ({ showLogout = true }) => {
  const collapsedAtom = useCollapsed();
  const setCollapsed = useSetCollapsed();
  const setOrgInfo = useSetOrgInfo();
  const orgInfo = useOrgInfo();
  const user = useUser();
  const [photo, setPhoto] = useState<string>('');
  const hasVoucher = useHasVoucher();
  const setHasVoucher = useSetHasVoucher();
  const notificationUnread = useParentNotificationUnreadCount();
  const refetchNotificationUnread = useRefetchParentNotificationUnreadCount();
  const voucherNotificationUnread = useParentVoucherNotificationsUnreadCount();
  const refetchVoucherNotificationUnread = useRefetchParentVoucherNotificationsUnreadCount();
  const profile = useProfile();
  const windowWidth = useWindowInnerWidth();
  const { t } = useTranslation();

  useEffect(() => {
    if (profile) {
      setPhoto(
        `${ApiUrl}/kindergarten/profile/photo/sm/${profile?.accelerator_foto}?t=${Date.now()}`
      );
    }

    if (!orgInfo) {
      setOrgInfo({ idOrganization: user?.org });
    }

    if (windowWidth > 768) {
      setCollapsed(true);
    }
  }, [profile, user]);

  useEffect(() => {
    if (user?.role?.includes('parent')) {
      setHasVoucher();
      refetchNotificationUnread();
      refetchVoucherNotificationUnread();
    }
  }, [user]);

  useEffect(() => {
    if (windowWidth <= 768) {
      if (collapsedAtom) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    }
  }, [collapsedAtom]);

  const routes = [
    {
      title: t('cm:pages.home'),
      path: RoutesUrls.home,
      icon: <HomeIcon />,
      show: ['accelerator', 'directorPreschool'],
      isTabbar: true,
    },
    {
      title: t('cm:pages.personal'),
      path: RoutesUrls.personal,
      icon: <PersonIcon />,
      show: ['accelerator', 'directorPreschool'],
      isTabbar: true,
    },
    ...(!orgInfo?.is_gov_queue
      ? [
          {
            title: t('cm:pages.ministries'),
            path: RoutesUrls.ministries,
            icon: <MinistriesIcon />,
            show: ['accelerator', 'directorPreschool'],
            isTabbar: true,
          },
        ]
      : []),
    {
      title: t('cm:pages.organization'),
      path: RoutesUrls.organization,
      icon: <OrganizationIcon />,
      show: ['accelerator', 'directorPreschool'],
      isTabbar: true,
    },
    ...(orgInfo?.id_ownership !== 1
      ? [
          {
            title: t('cm:pages.kindergartenVaucher'),
            path: RoutesUrls.kindergartenVaucher,
            icon: <VaucherIcon />,
            show: ['accelerator', 'directorPreschool'],
            isTabbar: true,
          },
        ]
      : []),
    {
      title: t('cm:pages.staff'),
      path: RoutesUrls.staff,
      icon: <EmployeesIcon />,
      show: ['accelerator', 'directorPreschool'],
      isTabbar: true,
    },
    {
      title: t('cm:pages.group'),
      path: RoutesUrls.group,
      icon: <GroupIcon />,
      show: ['accelerator', 'directorPreschool'],
      isTabbar: false,
    },
    ...(orgInfo?.is_departmental_queue
      ? [
          {
            title: t('cm:pages.departments'),
            path: RoutesUrls.departments,
            icon: <OrganizationIcon />,
            show: ['accelerator', 'directorPreschool'],
            isTabbar: false,
          },
        ]
      : []),
    {
      title: t('cm:pages.kids'),
      path: RoutesUrls.kids,
      icon: <KidsIcon />,
      show: ['accelerator', 'directorPreschool', 'teacher'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.applications'),
      path: RoutesUrls.applications,
      icon: <EducationIcon />,
      show: ['accelerator', 'directorPreschool'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.attendance'),
      path: RoutesUrls.attendance,
      icon: <AttendanceIcon />,
      show: ['accelerator', 'teacher'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.attendanceReport'),
      path: RoutesUrls.attendanceReport,
      icon: <TableIcon />,
      show: ['accelerator', 'directorPreschool'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.training'),
      path: RoutesUrls.training,
      icon: <EducationIcon />,
      show: ['accelerator', 'directorPreschool', 'teacher'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.documents'),
      path: RoutesUrls.documents,
      icon: <DocIcon />,
      show: ['accelerator', 'directorPreschool', 'teacher'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.help'),
      path: RoutesUrls.help,
      icon: <HelpIcon />,
      show: ['accelerator', 'teacher'],
      isTabbar: false,
    },

    {
      title: t('cm:pages.registry'),
      path: RoutesUrls.adminRegistry,
      icon: <OrganizationIcon />,
      show: ['adminMES', 'adminMH'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.registry'),
      path: RoutesUrls.adminOrganization,
      icon: <OrganizationIcon />,
      show: ['adminME', 'employeeME', 'goronoDistrictOfSignification'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.documents'),
      path: RoutesUrls.monDocuments,
      icon: <DocsIcon />,
      show: ['adminME', 'employeeME'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.instructions'),
      path: RoutesUrls.adminInstructions,
      icon: <DocIcon />,
      show: ['adminME'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.employee'),
      path: RoutesUrls.employee,
      icon: <PersonIcon />,
      show: ['admin', 'adminMES', 'adminMH', 'adminME'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.aymakFinanceFood'),
      path: RoutesUrls.aymakFinanceFood,
      icon: <TableIcon />,
      show: ['admin', 'aymakFin'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.aymakFinanceOrganization'),
      path: RoutesUrls.aymakFinanceOrganization,
      icon: <VaucherIcon />,
      show: ['admin', 'aymakFin'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.aymakFinanceChildrenVouchers'),
      path: RoutesUrls.aymakFinanceChildrenVouchers,
      icon: <KidsIcon />,
      show: ['admin', 'aymakFin'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.monVoucher'),
      path: RoutesUrls.monVoucher,
      icon: <VaucherIcon />,
      show: ['adminME', 'employeeME', 'monFin'],
      isTabbar: true,
    },
    {
      title: t('cm:pages.adminParentPasswordRecovery'),
      path: RoutesUrls.adminParentPasswordRecovery,
      icon: <UpdateIcon />,
      show: ['adminME'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.adminChildApplicationSearch'),
      path: RoutesUrls.adminChildApplicationSearch,
      icon: <KidsIcon />,
      show: ['adminME'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.adminAccelerator'),
      path: RoutesUrls.adminAccelerator,
      icon: <PersonIcon />,
      show: ['adminME'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.monFinanceVoucher'),
      path: RoutesUrls.monFinanceVoucher,
      icon: <VaucherIcon />,
      show: ['monAccountant', 'bishkekFin'],
      isTabbar: true,
    },

    {
      title: t('cm:pages.bishkekVoucher'),
      path: RoutesUrls.bishkekVoucher,
      icon: <VaucherIcon />,
      show: ['employeeBishkek', 'bishkekFin'],
      isTabbar: true,
    },
    {
      title: t('cm:pages.bishkekHelp'),
      path: RoutesUrls.bishkekHelp,
      icon: <HelpIcon />,
      show: ['employeeBishkek', 'bishkekFin'],
      isTabbar: true,
    },
    {
      title: t('cm:pages.registry'),
      path: RoutesUrls.registry,
      icon: <OrganizationIcon />,
      show: ['employeeMES', 'employeeMH'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.parentCabinetHome'),
      path: RoutesUrls.parentCabinetHome,
      icon: <HomeIcon />,
      show: ['parent'],
      isTabbar: false,
    },
    ...(hasVoucher
      ? [
          {
            title: t('cm:pages.parentCabinetMembers'),
            path: RoutesUrls.parentCabinetMembers,
            icon: <ParentsMembers />,
            show: ['parent'],
            isTabbar: false,
          },
        ]
      : []),
    {
      title: t('cm:pages.parentCabinetPersonal'),
      path: RoutesUrls.parentCabinetPersonal,
      icon: <PersonIcon />,
      show: ['parent'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.parentCabinetEducation'),
      path: RoutesUrls.parentCabinetEducation,
      icon: <EducationIcon />,
      show: ['parent'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.kids'),
      path: RoutesUrls.parentCabinetChildrenList,
      icon: <KidsIcon />,
      show: ['parent'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.attendance'),
      path: RoutesUrls.parentCabinetAttendance,
      icon: <AttendanceIcon />,
      show: ['parent', 'member'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.parentCabinetNotifications'),
      path: RoutesUrls.parentCabinetNotifications,
      icon: <NotificationIcon />,
      show: ['parent'],
      isTabbar: false,
      badgeCount: (notificationUnread || 0) + (voucherNotificationUnread || 0),
    },
    ...(hasVoucher
      ? [
          {
            title: t('cm:pages.parentCabinetVoucher'),
            path: RoutesUrls.parentCabinetVoucher,
            icon: <ParentsVoucherRoute />,
            show: ['parent'],
            isTabbar: false,
          },
        ]
      : []),
    // {
    //   title: t('cm:pages.parentCabinetPayment'),
    //   path: RoutesUrls.parentCabinetPayment,
    //   icon: <PaymentIcon />,
    //   show: ['parent'],
    //   isTabbar: false,
    // },
    // {
    //   title: t('cm:pages.parentCabinetInstruction'),
    //   path: RoutesUrls.parentCabinetInstruction,
    //   icon: <HelpIcon />,
    //   show: ['parent'],
    //   isTabbar: false,
    // },
    {
      title: t('cm:pages.reports'),
      path: RoutesUrls.reports,
      icon: <DocIcon />,
      // районное гороно видит здесь только вкладку «Аномалии» по своим районам
      show: ['adminME', 'employeeME', 'goronoDistrictOfSignification'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.control'),
      path: RoutesUrls.control,
      icon: <SettingsIcon />,
      show: ['adminME', 'employeeME'],
      isTabbar: false,
    },

    {
      title: t('cm:pages.voucherApplication'),
      path: RoutesUrls.voucherApplication,
      icon: <FileIcon />,
      show: ['chairman', 'secretary'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.voucherCommission'),
      path: RoutesUrls.voucherCommission,
      icon: <PersonIcon />,
      show: ['chairman', 'secretary'],
      isTabbar: false,
    },
    // {
    //   title: t('cm:pages.voucherAymakAgreement'),
    //   path: RoutesUrls.voucherAymakAgreement,
    //   icon: <AymakAgreementIcon />,
    //   show: ['chairman', 'secretary'],
    //   isTabbar: false,
    // },
    {
      title: t('cm:pages.voucherAymakFood'),
      path: RoutesUrls.voucherAymakFood,
      icon: <FoodIcon />,
      show: ['chairman', 'secretary'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.voucherAdmin'),
      path: RoutesUrls.voucherAdmin,
      icon: <VaucherIcon />,
      show: ['chairman', 'secretary'],
      isTabbar: true,
    },
    {
      title: t('cm:pages.monHelp'),
      path: RoutesUrls.monHelp,
      icon: <HelpIcon />,
      show: ['adminME', 'employeeME', 'monFin'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.parentHelp'),
      path: RoutesUrls.parentHelp,
      icon: <HelpIcon />,
      show: ['parent'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.helpVoucher'),
      path: RoutesUrls.helpVoucher,
      icon: <HelpIcon />,
      show: ['chairman', 'secretary'],
      isTabbar: false,
    },
    {
      title: t('cm:pages.feedback'),
      path: RoutesUrls.feedback,
      icon: <FeedbackIcon />,
      show: [
        'accelerator',
        'directorPreschool',
        'adminMES',
        'adminMH',
        'adminME',
        'bishkekFin',
        'employeeBishkek',
        'employeeME',
        'admin',
        'aymakFin',
        'monFin',
        'employeeMES',
        'employeeMH',
        'parent',
        'member',
        'chairman',
        'secretary',
        'teacher',
      ],
      isTabbar: false,
    },
    {
      title: t('cm:pages.instructions'),
      path: RoutesUrls.instructions,
      icon: <HelpIcon />,
      show: [
        'admin',
        'adminMES',
        'adminMH',
        'adminME',
        'monFin',
        'monAccountant',
        'bishkekFin',
        'employeeMES',
        'employeeMH',
        'employeeME',
        'employeeBishkek',
        'accelerator',
        'directorPreschool',
        'teacher',
        'parent',
        'member',
        'chairman',
        'secretary',
        'commission',
        'aymakFin',
        'goronoRepublicanOfSignification',
        'goronoRegionOfSignification',
        'goronoDistrictOfSignification',
      ],
      isTabbar: false,
    },
  ];

  const settings = [
    {
      title: t('cm:pages.settings'),
      path: RoutesUrls.settings,
      icon: <SettingsIcon />,
    },

    // {
    //   title: t('cm:pages.privacyPolicy'),
    //   path: RoutesUrls.privacyPolicy,
    //   icon: <PrivacyPolicyIcon />,
    // },
  ];

  const handleClickButton = () => {
    if (windowWidth <= 768) {
      setCollapsed(!collapsedAtom);
    }
  };

  return (
    <>
      <Sider
        user={
          <SiderUser
            photo={photo}
            fio={`${user?.s} ${user?.n?.charAt(-0)}. ${user?.p ? user?.p.charAt(0) + '.' : ''}`}
            role={t(`role.${user?.role[0]}`)}
            onError={<SN surname={user?.s || ''} name={user?.n || ''} size={18} />}
          />
        }
        activeLink={showLogout}
        routes={routes
          .filter((x) => x.show?.some((y) => user!.role.includes(y as roleType)))
          .map((item) => {
            return (
              <SiderButton
                key={item.path}
                path={item.path}
                title={item.title}
                icon={item.icon}
                collapsed={collapsedAtom}
                onClick={handleClickButton}
                badgeCount={(item as any).badgeCount}
              />
            );
          })}
        // links={<LinksView />}
        links={null}
        settings={
          <>
            {settings?.map((item) => {
              return (
                <SiderSettingsButton
                  key={item.path}
                  path={item.path}
                  title={item.title}
                  icon={item.icon}
                  collapsed={collapsedAtom}
                />
              );
            })}
            {showLogout ? (
              <SiderSettingsButton
                path={RoutesUrls.logout}
                title={t('cm:pages.logout') as string}
                icon={<LogoutIcon />}
                collapsed={collapsedAtom}
                color="red"
              />
            ) : null}
          </>
        }
        collapsed={collapsedAtom}
      />

      {/* {windowWidth <= 768 && user?.type === 1 ? (
        <TabBar
          routes={routes.map((item) => {
            return <TabbarButton key={item.path} path={item.path} icon={item.icon} />;
          })}
        />
      ) : null} */}
    </>
  );
};
